import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Newspaper, Scale, Stethoscope, GraduationCap, UserPlus, HandHelping, Handshake, Sparkles, CheckCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ICON_BY_KIND = {
  postagem: Newspaper,
  direito: Scale,
  diagnostico: Stethoscope,
  curso: GraduationCap,
  associado: UserPlus,
  apoiador: HandHelping,
  parceiro: Handshake,
  post: Sparkles,
};

const COLOR_BY_KIND = {
  postagem: "#4A7C59",
  direito: "#2563eb",
  diagnostico: "#0ea5e9",
  curso: "#7c3aed",
  associado: "#E89D7D",
  apoiador: "#f59e0b",
  parceiro: "#0f766e",
  post: "#4A7C59",
};

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return "agora";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} h`;
    const d = Math.floor(h / 24);
    return `${d} d`;
  } catch { return ""; }
}

export default function NotificationBell({ testid = "notif-bell" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch { /* silencioso — sino não deve quebrar o header */ }
  }, [user]);

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    fetchNotifs();
    // Poll leve a cada 60s pra manter o contador vivo sem custo alto.
    const id = setInterval(fetchNotifs, 60000);
    return () => clearInterval(id);
  }, [user, fetchNotifs]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setUnread(0);
    } catch { /* ignore */ }
  };

  // Quando abre o painel, marca tudo como lido depois de 1s (dá tempo do usuário ver o badge).
  useEffect(() => {
    if (!open || unread === 0) return;
    const t = setTimeout(() => markAllRead(), 1200);
    return () => clearTimeout(t);
  }, [open, unread]);

  const badge = useMemo(() => {
    if (unread <= 0) return null;
    return unread > 9 ? "9+" : String(unread);
  }, [unread]);

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Notificações"
          data-testid={testid}
          className="relative inline-flex items-center justify-center h-10 w-10 rounded-full border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))] transition-colors"
        >
          <Bell className="h-5 w-5" />
          {badge && (
            <span
              data-testid="notif-bell-badge"
              className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-[hsl(var(--destructive))] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white dark:border-[hsl(var(--card))]"
            >
              {badge}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[22rem] max-w-[calc(100vw-1.5rem)] rounded-2xl p-0 overflow-hidden"
        data-testid="notif-panel"
      >
        <div className="px-4 py-3 flex items-center justify-between border-b border-[hsl(var(--border))]">
          <div className="font-heading font-bold text-sm">Notificações</div>
          <button
            type="button"
            onClick={markAllRead}
            disabled={unread === 0}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline disabled:opacity-40 disabled:no-underline"
            data-testid="notif-mark-all-read"
          >
            <CheckCheck className="h-3.5 w-3.5" /> Limpar todas
          </button>
        </div>
        <div className="max-h-[24rem] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
              Nada por aqui ainda. Volte em breve! 🌱
            </div>
          ) : (
            items.map((it) => {
              const Icon = ICON_BY_KIND[it.kind] || Sparkles;
              const color = COLOR_BY_KIND[it.kind] || "#4A7C59";
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    if (it.link) navigate(it.link);
                  }}
                  className="w-full text-left px-4 py-3 flex items-start gap-3 border-b border-[hsl(var(--border))]/60 last:border-b-0 hover:bg-[hsl(var(--muted))]/60 transition-colors"
                  data-testid={`notif-item-${it.id}`}
                >
                  <div
                    className="h-9 w-9 rounded-full grid place-items-center text-white shrink-0"
                    style={{ background: color }}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold leading-tight line-clamp-2">{it.title}</div>
                    {it.subtitle && (
                      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-1">{it.subtitle}</div>
                    )}
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{timeAgo(it.created_at)}</div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
